import { Loader, Checkbox as MantineCheckbox, Table } from '@mantine/core'
import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { isEmpty } from '@semios/app-platform-common'
import type { TLngLat, TWindMachineDetailsElement } from 'App/Map/types'
import { Button } from 'components/Button/Button'
import { Checkbox } from 'components/Checkbox/Checkbox'
import { IconControlTemperature } from 'components/icons/IconControlsInfo/IconControlTemperature'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { NumberInput } from 'components/NumberInput/NumberInput'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import React, { useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { apiFetch } from 'utils/apiFetch'
import { sortByKey } from 'utils/sortByKey'
import { unitConverter } from 'utils/unitConverter/unitConverter'

export const WindMachineAutoStartStopSettings = ({
  content,
  fetchData,
}: {
  content: TWindMachineDetailsElement
  fetchData: () => void
}) => {
  const isImperial = userDetailsStore.getState().temperature === 'IMPERIAL'
  const [selectedLngLats, setSelectedLngLats] = useState<string[]>([])
  const [startTemp, setStartTemp] = useState<number | ''>(isImperial ? 36 : 2)
  const [stopTemp, setStopTemp] = useState<number | ''>(isImperial ? 40 : 4)
  const [windMachineStartStopOpened, setWindMachineStartStopOpened] = useState(false)
  const property = selectedFieldAssetsStore.useSelector((s) => s.property)

  const [hasFormErrors, setHasFormErrors] = useState<{
    startTemp: boolean
    stopTemp: boolean
    devices: boolean
  }>({
    startTemp: false,
    stopTemp: false,
    devices: false,
  })

  useEffect(() => {
    const errors = {
      startTemp: isNil(startTemp) || isEmpty(startTemp) || Number(startTemp) >= Number(stopTemp),
      stopTemp: isNil(stopTemp) || isEmpty(stopTemp) || Number(stopTemp) <= Number(startTemp),
      devices: selectedLngLats.length === 0,
    }

    setHasFormErrors(errors)
  }, [selectedLngLats, startTemp, stopTemp])

  const handleChange = (newValue: number | '', name: string) => {
    switch (name) {
      case 'startTemp':
        setStartTemp(newValue)

        break

      case 'stopTemp':
        setStopTemp(newValue)

        break

      default:
        break
    }
  }

  const handleOnClick = async () => {
    if (!selectedLngLats) return

    if (startTemp === '' || stopTemp === '') return

    try {
      const settings: { tempStart?: number; tempStop?: number } = {}
      const inputUnit = isImperial ? 'f' : 'c'

      const cleanedStartTemp = unitConverter
        .windMachineTemperature(startTemp, { inputUnit, outputUnit: 'c', decimalPlaces: 3 })
        .value()

      const cleanedStopTemp = unitConverter
        .windMachineTemperature(stopTemp, { inputUnit, outputUnit: 'c', decimalPlaces: 3 })
        .value()

      if (isNil(cleanedStartTemp) || isNil(cleanedStopTemp)) throw new Error('Invalid Temperature Conversion')

      settings.tempStart = cleanedStartTemp

      settings.tempStop = cleanedStopTemp

      const request = await apiFetch({
        url: '/wind-machine',
        body: {
          windMachineSettings: {
            lngLats: selectedLngLats,
            settings,
          },
        } as routes.WindMachine.Request,
      })

      if (isNil(request?.windMachineSettings)) throw new Error('Failed to update settings')

      fetchData()
    } catch (error) {
      return
    }
  }

  const confirmChangesStart = (intentional: boolean) => {
    if (intentional) handleOnClick()

    setWindMachineStartStopOpened(false)
  }

  const windMachineItems =
    content?.windMachineDetails
      ?.map((windMachine) => {
        const lngLat = `POINT(${windMachine.geom.coordinates[0]} ${windMachine.geom.coordinates[1]})`

        return {
          name: windMachine.equipmentDeviceNumber,
          lngLat,
          tempStart: windMachine.equipmentSettings.tempStart,
          tempStop: windMachine.equipmentSettings.tempStop,
          equipmentSettings: windMachine.equipmentSettings,
          applicationSettingsNewSettings: windMachine.applicationSettingsNew.settings,
          wmStatus: windMachine.applicationSettingsNew.status,
        }
      })
      .sort(sortByKey('name')) || []

  const renderWindMachines = windMachineItems.map(
    (item: {
      name: string
      lngLat: string
      tempStart: number | null
      tempStop: number | null
      equipmentSettings: { tempStart: number; tempStop: number }
      applicationSettingsNewSettings: { tempStart: number; tempStop: number }
      wmStatus: string | null
    }) => {
      const wmNewSettings = item?.applicationSettingsNewSettings

      let showLoading = true

      if ((isNil(wmNewSettings.tempStart) && isNil(wmNewSettings.tempStop)) || item.wmStatus === 'onDevice') {
        showLoading = false
      } else if (item.wmStatus === 'new') {
        showLoading =
          wmNewSettings?.tempStart !== item.equipmentSettings?.tempStart ||
          wmNewSettings?.tempStop !== item.equipmentSettings?.tempStop
      }

      let wmName: string | undefined

      const itemLngLat = item.lngLat as TLngLat

      if (!isNil(property) && !isNil(itemLngLat))
        wmName = fieldAssetStore.getState().properties?.[property]?.points?.[itemLngLat].name || item?.name

      if (isNil(wmName)) wmName = ''

      return (
        <tr
          key={item.lngLat}
          onClick={(e) => {
            e.stopPropagation()

            setSelectedLngLats((prev) =>
              prev.includes(item.lngLat)
                ? prev.filter((position) => position !== item.lngLat)
                : [...prev, item.lngLat],
            )
          }}
          style={{ height: 60 }}
        >
          <td>
            <Checkbox label={wmName} value={item.lngLat} style={{ zIndex: -1, position: 'relative' }} />
          </td>
          <td>
            {showLoading && <Loader size={10} style={{ marginRight: 4 }} />}
            {unitConverter.windMachineTemperature(item.tempStart).valueWithSuffix()}
          </td>
          <td>
            {showLoading && <Loader size={10} style={{ marginRight: 4 }} />}
            {unitConverter.windMachineTemperature(item.tempStop).valueWithSuffix()}
          </td>
        </tr>
      )
    },
  )

  const ErrorBlock = () => {
    if (hasFormErrors.startTemp || hasFormErrors.stopTemp) {
      return (
        <div
          css={{
            padding: 10,
            color: colors.red,
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {translate.phrases.banyanApp('Start temperature must be less than stop temperature')}
        </div>
      )
    }

    return <></>
  }

  return (
    <>
      <ModalDrawer
        title={translate.phrases.banyanApp('Set Start/Stop Temperatures')}
        opened={windMachineStartStopOpened}
        onClose={() => confirmChangesStart(false)}
        primaryButtonText={
          selectedLngLats.length > 0
            ? translate.phrases.banyanApp('Apply to {{numDevices}} Devices', {
                numDevices: selectedLngLats.length,
              })
            : translate.phrases.banyanApp('Select Devices')
        }
        primaryButtonOnPress={() => confirmChangesStart(true)}
        primaryButtonDisabled={Object.values(hasFormErrors).some(Boolean)}
        size={'500px'}
        zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
      >
        <div
          css={{
            padding: 20,
            color: colors.midnight,
            height: '100%',
            overflow: 'hidden',
            display: 'grid',
            gridTemplateRows: '155px',
          }}
        >
          <div style={{ gridRow: 1 }}>
            <h3 style={{ marginTop: 0 }}>{translate.phrases.banyanApp('Set Start/Stop Temperatures')}</h3>

            <div
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                paddingRight: 20,
              }}
            >
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '10px 0px',
                  fontSize: '12px',
                }}
              >
                <label
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '10px',
                  }}
                >
                  {translate.phrases.banyanApp('Start Temperature')}
                </label>
                <NumberInput
                  css={{ maxWidth: 100 }}
                  value={startTemp}
                  onChange={(e) => handleChange(e, 'startTemp')}
                  styles={{
                    input: {
                      padding: 0,
                      textAlign: 'center',
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      border: 'solid 2px #B4B3BB',
                    },
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  hideControls
                />
                <span
                  css={{
                    display: 'flex',
                    padding: '10px',
                    alignItems: 'center',
                    background: '#B4B3BB',
                    borderTopRightRadius: '3px',
                    borderBottomRightRadius: '3px',
                  }}
                >
                  {unitConverter.windMachineTemperature().suffix()}
                </span>
              </div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'row',
                  padding: '10px',
                  fontSize: '12px',
                }}
              >
                <label
                  css={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '10px',
                  }}
                >
                  {translate.phrases.banyanApp('Stop Temperature')}
                </label>
                <NumberInput
                  css={{ maxWidth: 100 }}
                  value={stopTemp}
                  onChange={(e) => handleChange(e, 'stopTemp')}
                  styles={{
                    input: {
                      padding: 0,
                      textAlign: 'center',
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0',
                      border: 'solid 2px #B4B3BB',
                    },
                  }}
                  decimalScale={2}
                  fixedDecimalScale
                  hideControls
                />
                <span
                  css={{
                    display: 'flex',
                    padding: '10px',
                    alignItems: 'center',
                    background: '#B4B3BB',
                    borderTopRightRadius: '3px',
                    borderBottomRightRadius: '3px',
                  }}
                >
                  {unitConverter.windMachineTemperature().suffix()}
                </span>
              </div>
            </div>
            <ErrorBlock />
            <h3>{translate.phrases.banyanApp('For the following devices')}</h3>
          </div>

          <div style={{ gridRow: 2, overflowY: 'scroll', overflowX: 'scroll', scrollbarWidth: 'none' }}>
            <MantineCheckbox.Group value={selectedLngLats} onChange={setSelectedLngLats} css={{ width: 500 }}>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '50%', textAlign: 'start' }}>
                      {translate.phrases.banyanApp('Wind Machine')}
                    </th>
                    <th style={{ width: '25%', textAlign: 'start' }}>
                      <span style={{ paddingRight: 5 }}>
                        <IconControlTemperature />
                      </span>
                      {translate.phrases.banyanApp('Start')}
                    </th>
                    <th style={{ width: '25%', textAlign: 'start' }}>
                      <span style={{ paddingRight: 5 }}>
                        <IconControlTemperature />
                      </span>
                      {translate.phrases.banyanApp('Stop')}
                    </th>
                  </tr>
                </thead>
                <tbody style={{ borderBottom: '0.0625rem solid #dee2e6', overflowY: 'auto' }}>
                  {renderWindMachines}
                </tbody>
              </Table>
            </MantineCheckbox.Group>
          </div>
        </div>
      </ModalDrawer>
      <Button
        css={{ width: '100%' }}
        variant={'tertiary'}
        onClick={() => setWindMachineStartStopOpened(true)}
      >
        {translate.phrases.banyanApp('Auto Start/Stop Settings')}
      </Button>
    </>
  )
}
